import { apiClient } from '@/utils/api-client';
import type { ApiResponse, JsonApiResource } from '@/types/api';

// Portal User types based on Rails model
export interface PortalUser {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  title: string | null;
  customer_type_id: number;
  customer_type_name: string;
  account_id: string | null;
  account_name: string | null;
  account?: {
    id: string;
    name: string;
    uses_saml_login: boolean;
    supresses_emails: boolean;
  } | null;
  affiliate_id: string | null;
  affiliate_name: string | null;
  is_prosci_certified: boolean;
  three_day_certified_date: string | null;
  ttt_tier: number | null;
  ttt_level: number | null;
  opt_in: boolean;
  notes: string | null;
  total_products: number;
  tool_count: number;
  premium_tools_count: number;
  can_edit: boolean;
  can_delete: boolean;
  available_actions?: string[];
  created: string | null;
  modified: string | null;
  // Detailed fields
  products?: UserProduct[];
  available_products?: AvailableProduct[];
  product_feature_assignments?: ProductFeatureAssignment[];
  credly_badge?: CredlyBadgeStatus;
}

export interface CredlyBadgeStatus {
  opted_in: boolean;
  opted_in_at: string | null;
  state: string | null;
  expires_at: string | null;
  email: string | null;
  badge_id: string | null;
  image_url: string | null;
  badge_url: string | null;
  sync_error: string | null;
}

export interface UserProduct {
  id: string;
  name: string;
  code: string;
  type: string;
  is_premium: boolean;
  expiration_date: string | null;
  // Assigned, but access is void because the org license it is linked to expired
  org_license_blocked?: boolean;
  // Set when the organization did not grant this assignment. License admins see it read-only.
  outside_license_reason?: 'self_purchased' | 'program_access' | 'not_licensed' | null;
  source: string;
  created_at: string;
}

export interface ProductFeature {
  code: string;
  name: string;
  /** The product that declares this feature — a grouped product lists its children's features. */
  product_id?: string;
  settings?: {
    admin_level?: number[];
    [key: string]: any;
  };
}

export interface ProductFeatureAssignment {
  product_id: string;
  feature_code: string;
  feature_name: string;
}

export interface AvailableProduct {
  id: string;
  name: string;
  code: string;
  category: string | null;
  type: string;
  available_seats: number | null;
  total_seats: number | null;
  allow_auto_assignment: boolean;
  is_grouped_product: boolean;
  related_products?: Array<{
    id: string;
    name: string;
    group?: string;
  }> | null;
  product_features: ProductFeature[];
  org_license_expired: boolean;
  org_license_expiration_date: string | null;
}

export interface PortalUserListParams {
  q?: string;                      // Search query (user name/email)
  org_search?: string;             // Search by organization name
  account_id?: string;             // Filter by organization ID
  customer_type?: number;          // Filter by customer type
  product_id?: string;             // Filter by product
  affiliate_id?: string;           // Filter by affiliate
  is_prosci_certified?: boolean;   // Filter by certification status
  limit?: number;
  skip?: number;
}

export interface PortalUserCreateParams {
  first_name: string;
  last_name: string;
  email: string;
  title?: string;
  customer_type_id: number;
  account_id?: string;
  affiliate_id?: string;
  is_prosci_certified?: boolean;
  // null explicitly clears the stored date; undefined leaves it untouched
  three_day_certified_date?: string | null;
  ttt_tier?: number;
  ttt_level?: number;
  opt_in?: boolean;
  notes?: string;
  product_ids?: string[];
}

export interface ProductMetadata {
  [productId: string]: {
    expiration_date?: string | null;
    features?: string[];
    // Grant this outside the expired org license, as a direct assignment
    override_org_license?: boolean;
  };
}

export interface PortalUserUpdateParams extends Partial<PortalUserCreateParams> {
  product_ids?: string[];
  product_metadata?: ProductMetadata;
}

// User service for managing portal users
export const userService = {
  // List all portal users with filtering and pagination
  async listUsers(params?: PortalUserListParams): Promise<{
    data: JsonApiResource<PortalUser>[];
    meta: {
      total_count: number;
      limit: number;
      skip: number;
    };
  }> {
    const queryParams: Record<string, any> = {};
    
    if (params?.q) queryParams.q = params.q;
    if (params?.org_search) queryParams.org_search = params.org_search;
    if (params?.account_id) queryParams.account_id = params.account_id;
    if (params?.customer_type !== undefined) queryParams.customer_type = params.customer_type;
    if (params?.product_id) queryParams.product_id = params.product_id;
    if (params?.affiliate_id) queryParams.affiliate_id = params.affiliate_id;
    if (params?.is_prosci_certified !== undefined) {
      queryParams.is_prosci_certified = params.is_prosci_certified ? 'true' : 'false';
    }
    if (params?.limit) queryParams.limit = params.limit;
    if (params?.skip) queryParams.skip = params.skip;
    
    return apiClient.get('/api/admin/users', queryParams);
  },

  // Get specific user with details
  async getUser(id: string): Promise<ApiResponse<JsonApiResource<PortalUser>>> {
    return apiClient.get(`/api/admin/users/${id}`);
  },

  // Create a new user
  async createUser(params: PortalUserCreateParams & { product_metadata?: ProductMetadata }): Promise<ApiResponse<JsonApiResource<PortalUser>>> {
    const { product_ids, product_metadata, ...attributes } = params;

    const body: any = {
      data: {
        type: 'users',
        attributes
      }
    };

    if (product_ids) {
      body.product_ids = product_ids;
    }

    if (product_metadata) {
      body.product_metadata = product_metadata;
    }

    return apiClient.post('/api/admin/users', body);
  },

  // Update an existing user
  async updateUser(id: string, params: PortalUserUpdateParams): Promise<ApiResponse<JsonApiResource<PortalUser>>> {
    const { product_ids, product_metadata, ...attributes } = params;

    const body: any = {
      data: {
        type: 'users',
        id,
        attributes
      }
    };

    if (product_ids !== undefined) {
      body.product_ids = product_ids;
    }

    if (product_metadata !== undefined) {
      body.product_metadata = product_metadata;
    }

    return apiClient.patch(`/api/admin/users/${id}`, body);
  },

  // Delete a user
  async deleteUser(id: string): Promise<void> {
    return apiClient.delete(`/api/admin/users/${id}`);
  },

  // Get the number of Proxima projects this user currently owns. Used to warn
  // admins about reassignment impact in the delete-user confirmation modal.
  async getOwnedProximaProjectsCount(id: string): Promise<{ count: number }> {
    return apiClient.get(`/api/admin/users/${id}/owned_proxima_projects_count`);
  },

  // Get available products for a new user based on account/customer type
  async getAvailableProducts(params: {
    account_id?: string;
    customer_type_id?: number;
  }): Promise<{ data: AvailableProduct[] }> {
    const queryParams: Record<string, any> = {};
    if (params.account_id) queryParams.account_id = params.account_id;
    if (params.customer_type_id) queryParams.customer_type_id = params.customer_type_id;

    return apiClient.get('/api/admin/users/available_products', queryParams);
  },

  // Send password reset email to user
  async sendPasswordReset(id: string): Promise<{ message: string }> {
    return apiClient.post(`/api/admin/users/${id}/send_password_reset`, {});
  },

  // Send welcome/notification email to user
  async sendWelcomeEmail(id: string): Promise<{ message: string }> {
    return apiClient.post(`/api/admin/users/${id}/send_welcome_email`, {});
  },

  // Masquerade as a user (super admin only)
  // Creates a masquerade session and returns the portal URL for redirect
  async masqueradeAsUser(userId: string): Promise<{
    success: boolean;
    redirect_url: string;
    user: {
      id: string;
      email: string;
      name: string;
    };
  }> {
    return apiClient.post(`/api/users/masquerade/${userId}`, {});
  },
};